import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const products = [
  {
    name: 'SynteraCRM',
    slug: 'synteracrm',
    sub: 'Business Software',
    tag: 'BESTSELLER',
    tagColor: '#FA6E4F',
    bg: '#F8CA9D',
    desc: 'A powerful customer relationship management platform designed to streamline sales pipelines, automate follow-ups, and deliver real-time analytics for smarter business decisions.',
  },
  {
    name: 'SynteraCloud',
    slug: 'synteracloud',
    sub: 'Infrastructure',
    tag: 'POPULAR',
    tagColor: '#8EC9BB',
    bg: '#C5D7C0',
    desc: 'Scalable cloud infrastructure with auto-scaling, serverless computing, and enterprise-grade security — built to grow with your business without downtime.',
  },
  {
    name: 'SynteraAnalytics',
    slug: 'synteraanalytics',
    sub: 'Data & AI',
    tag: 'NEW',
    tagColor: '#FB8E7E',
    bg: '#FB8E7E',
    desc: 'AI-powered analytics suite that transforms raw data into actionable insights with predictive modelling, custom dashboards, and automated reporting.',
  },
  {
    name: 'SynteraShield',
    slug: 'synterashield',
    sub: 'Security',
    tag: 'PREMIUM',
    tagColor: '#F2CF59',
    bg: '#F2CF59',
    desc: 'End-to-end cybersecurity protection including threat detection, vulnerability scanning, encryption management, and 24/7 incident response monitoring.',
  },
  {
    name: 'SynteraLaunch',
    slug: 'synteralaunch',
    sub: 'Marketing Suite',
    tag: 'BUNDLE',
    tagColor: '#FA6E4F',
    bg: '#8EC9BB',
    desc: 'All-in-one digital marketing toolkit with campaign automation, social media scheduling, SEO tracking, and performance analytics to maximise your reach.',
  },
];

export default function ProductsSection() {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'right' ? 500 : -500, behavior: 'smooth' });
    }
  };

  return (
    <section ref={sectionRef} className="w-full h-screen flex overflow-hidden relative" style={{ background: '#111' }}>
      {/* Left label */}
      <div className="flex-shrink-0 w-48 h-full flex items-center justify-center relative z-10" style={{ background: '#8EC9BB' }}>
        <p
          className="boldonse text-white select-none"
          style={{
            fontSize: 'clamp(40px, 6vw, 80px)',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            transform: 'rotate(180deg)',
            letterSpacing: '-2px',
          }}
        >
          TOOLS
        </p>
      </div>

      {/* Products horizontal scroll */}
      <div
        ref={scrollRef}
        className="flex-1 flex items-center gap-6 overflow-x-auto px-20 py-12"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((p, i) => (
          <Link
            key={p.name}
            to={`/products`}
            className={`flex-shrink-0 rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 block ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
            style={{
              width: '340px',
              height: '480px',
              background: p.bg,
              transitionDelay: `${i * 100}ms`,
              transform: hovered === i ? 'scale(1.04) translateY(-8px)' : 'scale(1) translateY(0)',
              textDecoration: 'none',
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            {/* Tag */}
            <div className="px-6 pt-6 flex justify-between items-center">
              <span
                className="boldonse text-xs px-3 py-1 rounded-full text-white whitespace-nowrap"
                style={{ background: p.tagColor }}
              >
                {p.tag}
              </span>
            </div>

            {/* Info */}
            <div className="px-6 pt-6 flex flex-col h-[calc(100%-80px)]">
              <p className="text-white/60 text-xs tracking-widest uppercase">{p.sub}</p>
              <p className="boldonse text-white text-2xl mt-2 leading-tight">{p.name}</p>
              <p className="text-white/80 text-sm leading-relaxed mt-4 flex-1">{p.desc}</p>

              {/* Bottom accent line */}
              <div className="mt-auto pt-4">
                <div
                  className="h-1 rounded-full w-16 transition-all duration-300"
                  style={{
                    background: p.tagColor,
                    width: hovered === i ? '100%' : '64px',
                  }}
                />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
